import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { Message, MessageService } from 'primeng/api';
import { Observable, of } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { BaseComponent } from '../../../../../shared/base-components/base.component';
import { ValidationStateModel } from '../../../../../shared/models/validation-results/validation-state.model';
import { ValidationModel } from '../../../../../shared/models/validation-results/validation.model';
import { VehicleAddComponent } from '../../../../../vehicle/components/shared/vehicle/vehicle-add/vehicle-add.component';
import { VehicleModel } from '../../../../../vehicle/models/vehicle.model';
import { VehicleService } from '../../../../../vehicle/services/vehicle.service';
import { VINType } from '../../../../models/enums/v-i-n-type.enum';

export class VehicleAddModalData {
    constructor(
        public vehicle: VehicleModel,
        public searchString: string,
        public isVIN: boolean,
        public title: string,
        public isEdit: boolean,
        public hasTitle: boolean = false,
        public isAROSPI: boolean = false,
        public isRegExpired: boolean = false
    ) {
    }
}

@Component({
    selector: 'vehicle-add-modal',
    templateUrl: './vehicle-add-modal.html'
})
export class VehicleAddModalComponent extends BaseComponent implements OnInit {
    public dataLoaded = false;
    public validationModel: ValidationModel = new ValidationModel([]);
    public showLaunchUpdateRegistration: boolean;
    public growlMessages: Message[] = [];
    public isVehicleAdded: boolean;
    public title: string;
    public searchString: string; // the string passed from the search component
    public isEdit: boolean;
    public isVIN: boolean;
    public vehicle: VehicleModel; // figure out how to populate this one.
    private originalVehicle: VehicleModel;
    private vinType: VINType = new VINType();

    @ViewChild(VehicleAddComponent)
    public _vehicleAddComponent: VehicleAddComponent;

    constructor(
        public dialogRef: MatDialogRef<VehicleAddModalComponent>,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: VehicleAddModalData,
        public vehicleService: VehicleService,
        private toastService: MessageService
    ) {
        super();
        this.searchString = this.data.searchString;
        this.title = this.data.title;
        this.isVIN = this.data.isVIN;
        this.growlMessages = new Array<Message>();
    }

    // Initialize the class
    public ngOnInit(): void {
        if (this.isVIN) {
            this.vehicle = this.data.vehicle;
            this.originalVehicle = JSON.parse(JSON.stringify(this.vehicle));
        } else {
            this.vehicle = new VehicleModel();
        }
        // show any validation when modal is loaded
        this.isVehicleAdded = false;
        this.dataLoaded = true;
    }

    public cancel(): void {
        if (this.isVehicleAdded) {
            this.dialogRef.close(this.vehicle.id);
        } else {
            this.dialogRef.close();
        }
    }

    public saveVehicle = (): void => {
        this._vehicleAddComponent.validateModel()
            .pipe(mergeMap(e => {
                if (e.length == 0 && this._vehicleAddComponent.isValid()) {
                    this.vehicle = this._vehicleAddComponent.vehicle; // TODO: see why 2-way binding is not working

                    if (this.criticalFieldsChanged()) {
                        return this.isDuplicateVin(this.vehicle.id, this.vehicle.vin, this.vehicle.vinTypeID)
                            .pipe(tap(dupe => {
                                if (dupe) {
                                    this.validationModel = this.createValidationMessage('State Assigned VIN Already in Use.  Assign Another Number.');
                                } else if (!this.validateStateAssignedVIN(this.vehicle.vin, this.vehicle.vinTypeID)) {
                                    this.validationModel = this.createValidationMessage('AZ State Assigned VIN should be formatted as \'AZ######\'');
                                } else if (this.data.hasTitle) {
                                    this.validationModel = this.createValidationMessage('Vehicle has a current Arizona title.  Changing this data may require a new title to be issued.');
                                } else if (this.data.isAROSPI && !this.data.isRegExpired) {
                                    this.showLaunchUpdateRegistration = true;
                                    this.validationModel = this.createValidationMessage('Vehicle has a Registration. Changing this data may require an updated registration to be issued');
                                } else if (this.data.isAROSPI && this.data.isRegExpired) {
                                    this.validationModel = this.createValidationMessage('Vehicle has an expired Registration. Vehicle information may not be changed at this time.');
                                }
                            }));
                    } else {
                        if (!this.vehicle.bodyStyleTypeID) {
                            this.vehicle.bodyStyleTypeID = 0;
                        }
                        this.isVehicleAdded = true;
                        return this.vehicleService.saveVehicle(this.vehicle)
                            .pipe(tap((vehicle: VehicleModel) => {
                                this.vehicle.id = vehicle.id;
                                this.vehicle.vehicleNumber = vehicle.vehicleNumber;
                                var results = vehicle.validationResults;
                                this.validationModel = new ValidationModel(results);

                                if (results.length == 0) {
                                    this.toastService.add({ severity: 'success', summary: 'Success', detail: 'Vehicle saved successfully.' });
                                    this._vehicleAddComponent.addVehicleForm.disable();
                                    this._vehicleAddComponent._makeAutoComplete.disabled = true;
                                    this._vehicleAddComponent._modelAutoComplete.disabled = true;
                                    this._vehicleAddComponent.hasBeenSaved = true;

                                    this.dialogRef.close(this.vehicle.id);
                                } else {
                                    this.isVehicleAdded = false;
                                    const correctTitleMessage = results.find(x => x.errorMessage == 'Vehicle has a current Arizona title.  Changing this data may require a new title to be issued.');
                                }
                            }, err => console.log(err)));
                    }
                } else {
                    this._vehicleAddComponent.markAsTouched();
                }

                return of(null);
            }))
            .subscribe();
    };

    public launchCorrectTitle(): void {
        this.dialogRef.close(this.vehicle);
    }

    public launchUpdateRegistration(): void {
        this.dialogRef.close(this.vehicle);
    }

    public onValidationErrors(event: any): void {
        if (event && event.validationResults) {
            var results = event.validationResults;
        }
    }

    private isDuplicateVin = (vehicleId: number, vin: string, vinTypeId: number): Observable<boolean> => {
        if (+this.vehicle.vinTypeID === this.vinType.StateAssigned.id) {
            return this.vehicleService.isDuplicateVin(vehicleId, this.vehicle.vin, this.vehicle.vinTypeID);
        } else {
            return of(false);
        }
    };

    public createValidationMessage(message: string): ValidationModel {
        const tempErrors = [];
        const state: ValidationStateModel = new ValidationStateModel();
        state.validationType = 'Error';
        tempErrors.push({ errorMessage: message, state: state });
        return new ValidationModel(tempErrors);
    }

    public criticalFieldsChanged(): boolean {
        if (this.originalVehicle) {
            return this.vehicle.modelYear != this.originalVehicle.modelYear ||
                   this.vehicle.makeID != this.originalVehicle.makeID ||
                   this.vehicle.vin != this.originalVehicle.vin ||
                   this.vehicle.bodyStyleTypeID != this.originalVehicle.bodyStyleTypeID;
        }

        return false;
    }

    public validateStateAssignedVIN(value: string, vinTypeID: number): boolean {
        if (+vinTypeID === this.vinType.StateAssigned.id) {
            var reg = new RegExp('[A][Z]([0-9])+');
            if (reg.test(value)) {
                return value.length === 8;
            }

            return false;
        }

        return true;
    }
}
